import { useQuery } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";

/**
 * Power BI report embed details for the Snapshot page.
 *
 * `configured: false` is the expected state today, not an error — there are no Power BI
 * credentials yet. The backend serves this from env vars so the panel and all its wiring
 * exist now, and connecting the real report is a matter of setting POWERBI_REPORT_URL.
 * See portfolio-risk-concentrations/app/router/powerbi.py for why this is the URL model
 * rather than an AAD embed-token flow, and how the token flow slots in later without
 * changing this contract.
 */
export interface PowerBIReport {
  key: string;
  /** "url" today. An "aad" mode would carry a short-lived embed token instead. */
  mode: "url" | "aad";
  /** False until a report URL is set. The UI renders its empty state, not an error. */
  configured: boolean;
  title: string;
  embedUrl: string | null;
  reportId: string | null;
  workspaceId: string | null;
  /** The report's scope field as "Table/Column", if it has one. When present the page
   *  appends a Power BI URL filter so the report follows the Internal/External choice. */
  filterParam: string | null;
}

export function usePowerBIReport(key = "monitor") {
  return useQuery({
    queryKey: ["prc-powerbi-report", key],
    queryFn: () =>
      getJson<PowerBIReport>(`${PRC_PREFIX}powerbi/report?key=${encodeURIComponent(key)}`),
  });
}

/**
 * The embed URL with the scope filter appended, when the report declares one.
 *
 * Power BI's URL filter syntax is `?filter=Table/Column eq 'value'`, and it is the only
 * way to preselect a filter without an embed token. Returns the bare URL unchanged when
 * the report has no scope field, rather than guessing at a column name — a wrong filter
 * expression makes Power BI render an empty report with no explanation.
 */
export function powerBIUrlForScope(report: PowerBIReport | undefined, scopeLabel: string) {
  if (!report?.embedUrl) return undefined;
  if (!report.filterParam) return report.embedUrl;
  const sep = report.embedUrl.includes("?") ? "&" : "?";
  const expr = `${report.filterParam} eq '${scopeLabel.replace(/'/g, "''")}'`;
  return `${report.embedUrl}${sep}filter=${encodeURIComponent(expr)}`;
}
